import { useEffect, useState } from "react";
import { Download, Smartphone, Monitor, X } from "lucide-react";

/**
 * InstallAppButton — permanent install entrypoint shown in user profile pages.
 *
 * Three states:
 *  1. Browser supports beforeinstallprompt (Android Chrome/Edge/Samsung, desktop Chrome/Edge)
 *     → button triggers the native install prompt
 *  2. iOS Safari (no event available, but installable via Share → Add to Home Screen)
 *     → button opens instruction modal
 *  3. Browser without PWA install (Firefox, Safari desktop, in-app browsers)
 *     → button still appears but explains how to install manually
 *
 * Re-uses the deferredPrompt cached globally by InstallPrompt.jsx (see window.__pwaPrompt).
 */
export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(() => window.__pwaPrompt || null);
  const [installed, setInstalled] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) setInstalled(true);
    const onPrompt = (e) => { e.preventDefault(); window.__pwaPrompt = e; setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); window.__pwaPrompt = null; };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) {
    return (
      <div className="kw-card p-4 flex items-center gap-3 border-[#22C55E]/30 bg-[#22C55E]/5" data-testid="install-app-done">
        <Smartphone className="w-5 h-5 text-[#22C55E]" />
        <div>
          <div className="kw-overline text-[#22C55E]">App instalada</div>
          <div className="text-xs text-[#A3A3A3]">Estás a usar a versão instalada da KW Lead Growth League.</div>
        </div>
      </div>
    );
  }

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { console.debug("install prompt dismissed:", e?.message); }
      setDeferred(null);
      window.__pwaPrompt = null;
    } else {
      setShowHint(true);
    }
  };

  return (
    <>
      <div className="kw-card p-4 flex items-center gap-3" data-testid="install-app-panel">
        <Download className="w-5 h-5 text-[#E50000] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Instalar app</div>
          <div className="text-xs text-[#A3A3A3]">
            Adiciona a KGL ao ecrã principal para acesso instantâneo (sem barra do browser).
          </div>
        </div>
        <button onClick={handleClick} className="kw-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="install-app-btn">
          <Download className="w-3 h-3" /> Instalar
        </button>
      </div>
      {showHint && <InstallHintModal onClose={() => setShowHint(false)} />}
    </>
  );
}

function InstallHintModal({ onClose }) {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isMacSafari = /Safari/.test(ua) && /Mac OS X/.test(ua) && !/Chrome/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isAndroid = /Android/.test(ua);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="install-hint-modal" onClick={onClose}>
      <div className="kw-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {isIOS ? <Smartphone className="w-5 h-5 text-[#E50000]" /> : <Monitor className="w-5 h-5 text-[#E50000]" />}
            <h3 className="font-display text-xl font-black uppercase">Instalar a KGL</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isIOS && <IOSSteps />}
        {!isIOS && isMacSafari && <MacSafariSteps />}
        {!isIOS && isFirefox && <FirefoxSteps />}
        {!isIOS && isAndroid && <AndroidManualSteps />}
        {!isIOS && !isMacSafari && !isFirefox && !isAndroid && <DesktopChromeSteps />}
      </div>
    </div>
  );
}

function Step({ num, children }) {
  return (
    <div className="flex gap-3 mb-3">
      <div className="w-6 h-6 rounded-full bg-[#E50000] text-white text-xs font-bold flex items-center justify-center shrink-0">{num}</div>
      <div className="text-sm text-[#A3A3A3] flex-1">{children}</div>
    </div>
  );
}

function IOSSteps() {
  return (
    <>
      <p className="text-xs text-[#737373] mb-4">No Safari iOS, toca:</p>
      <Step num="1">Botão <strong className="text-white">Partilhar</strong> (no fundo do ecrã)</Step>
      <Step num="2">Desliza para baixo e escolhe <strong className="text-white">"Adicionar ao Ecrã Principal"</strong></Step>
      <Step num="3">Toca <strong className="text-white">"Adicionar"</strong> no canto superior direito</Step>
    </>
  );
}

function MacSafariSteps() {
  return (
    <>
      <p className="text-xs text-[#737373] mb-4">No Safari macOS:</p>
      <Step num="1">Menu <strong className="text-white">Ficheiro</strong> → <strong className="text-white">Adicionar à Dock</strong></Step>
      <Step num="2">Dá um nome e confirma — fica como app na Dock</Step>
      <Step num="3">Ou alternativamente usa o Chrome/Edge para "Instalar" como PWA verdadeira</Step>
    </>
  );
}

function FirefoxSteps() {
  return (
    <>
      <p className="text-xs text-[#737373] mb-4">O Firefox não suporta instalação PWA nativa. Recomendamos:</p>
      <Step num="1">Abre esta página no <strong className="text-white">Chrome</strong> ou <strong className="text-white">Edge</strong></Step>
      <Step num="2">Clica neste mesmo botão "Instalar"</Step>
      <Step num="3">Aceita o prompt nativo do browser</Step>
    </>
  );
}

function AndroidManualSteps() {
  return (
    <>
      <p className="text-xs text-[#737373] mb-4">O browser ainda não detectou a app como instalável. Tenta:</p>
      <Step num="1">Menu do browser (3 pontos ⋮)</Step>
      <Step num="2">Escolhe <strong className="text-white">"Instalar app"</strong> ou <strong className="text-white">"Adicionar ao ecrã principal"</strong></Step>
      <Step num="3">Confirma a instalação</Step>
    </>
  );
}

function DesktopChromeSteps() {
  return (
    <>
      <p className="text-xs text-[#737373] mb-4">No browser desktop:</p>
      <Step num="1">Procura o ícone de instalação 🖥️ na barra de endereço (à direita do URL)</Step>
      <Step num="2">Ou abre o menu do browser e escolhe <strong className="text-white">"Instalar KW Lead Growth League…"</strong></Step>
      <Step num="3">Confirma — a app abre numa janela própria sem barra do browser</Step>
    </>
  );
}
